import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Form, Input, message, notification, Table, Tag, Button, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, CheckOutlined } from '@ant-design/icons';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { amendProducts, updateCatalog } from '@app/api/prImportFreeSales.api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { useNavigate } from 'react-router-dom';
import { CatalogData } from '@app/models/feed_forecast/FeedForecastRequest';
import { updatedCatalog } from '@app/models/import_free_sales/ImpFreeSalesRequest';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface FormProps {
  editable: boolean;
  onSubmit: (data: any) => void;
  form: any;
  status: string | null;
  isAmmendable: boolean;
  pr_id: string | null;
  productListData: any;
  isCatEditable: boolean;
  product_type: string;
  pr_type?: string;
}

interface ProductEntry {
  key: string;
  productName: string;
  description: string;
  impQty?: number;
  manQty?: number;
  code?: string;
  product_id?: string;
  status: string;
}

const ProductListForm = React.forwardRef<any, FormProps>(
  (
    { editable, onSubmit, form, status, isAmmendable, pr_id, productListData, isCatEditable, product_type, pr_type },
    ref,
  ) => {
    const [productName, setProductName] = useState('');
    const [description, setDescription] = useState('');
    const [impQty, setImpQty] = useState('');
    const [manQty, setManQty] = useState('');
    const [genericInput, setGenericInput] = useState('');
    const [speciesInput, setSpeciesInput] = useState('');
    const [genericNames, setGenericNames] = useState<string[]>([]);
    const [species, setSpecies] = useState<string[]>([]);
    const [products, setProducts] = useState<ProductEntry[]>([]);

    const navigate = useNavigate();
    const isFormEditable = editable && (status === null || status === 'DRAFT');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userType = user?.usertype;
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [aafCodes, setAafCodes] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [editableInputs, setEditableInputs] = useState<Record<string, boolean>>({});

    const handleAddGenericName = () => {
      if (genericInput.trim()) {
        setGenericNames([...genericNames, genericInput.trim()]);
        setGenericInput('');
      }
    };

    const handleRemoveGenericName = (name: string) => {
      setGenericNames(genericNames.filter((n) => n !== name));
    };

    const handleAddSpecies = () => {
      if (speciesInput.trim()) {
        setSpecies([...species, speciesInput.trim()]);
        setSpeciesInput('');
      }
    };

    const handleRemoveSpecies = (name: string) => {
      setSpecies(species.filter((n) => n !== name));
    };

    const handleAddProduct = () => {
      if (productName.trim() && description.trim()) {
        setProducts([
          ...products,
          {
            key: Date.now().toString(),
            productName,
            description,
            // manQty: Number(manQty),
            // impQty: Number(impQty),
            status: '',
          },
        ]);
        setProductName('');
        setDescription('');
        // setImpQty('');
        // setManQty('');
        form.resetFields();
      }
    };

    const handleRemoveProduct = (key: string) => {
      setProducts(products.filter((p) => p.key !== key));
    };

    useEffect(() => {
      console.log('received catalog editability: ', isCatEditable);

      if (productListData) {
        console.log('Received productListData in props:', productListData);

        const catalogData = productListData.catalog || productListData;

        if (product_type !== null && product_type === 'MULTIPLE') {
          if (Array.isArray(catalogData)) {
            setProducts(
              catalogData.map((item: any) => ({
                key: item.product_id || '',
                productName: item.product_name || '',
                description: item.description || '',
                // impQty: item.imp_qty || '',
                // manQty: item.man_qty || '',
                status: item.status || '',
                code: item.code || '',
              })),
            );
          } else if (catalogData.length > 0) {
            setProducts([
              {
                productName: catalogData[0]?.product_name || '',
                description: catalogData[0]?.description || '',
                key: catalogData[0]?.product_id || '',
                // impQty: catalogData[0]?.imp_qty || '',
                // manQty: catalogData[0]?.man_qty || '',
                status: catalogData[0]?.status || '',
                code: catalogData[0]?.code || '',
              },
            ]);
          }
        } else {
          const product_name = catalogData.product_name || '';
          const generic_names = Array.isArray(catalogData.generic_names) ? catalogData.generic_names : [];
          const speciesData = Array.isArray(catalogData.species) ? catalogData.species : [];

          setProductName(product_name);
          setGenericNames(generic_names);
          setSpecies(speciesData);

          form.setFieldsValue({
            product_name: product_name,
            generic_names: generic_names,
            species: speciesData,
          });
        }
      }
    }, [productListData, product_type, form]);

    useImperativeHandle(
      ref,
      () => ({
        getData: () => {
          let formattedData = null;

          if (product_type !== null && product_type === 'MULTIPLE') {
            formattedData = products.map((product) => ({
              product_name: product.productName,
              description: product.description,
              // imp_qty: product.impQty,
              // man_qty: product.manQty,
            }));
          } else {
            formattedData = {
              product_name: productName,
              generic_names: genericNames,
              species: species,
            };
          }

          const result = {
            catalog: product_type !== null && product_type === 'MULTIPLE' ? formattedData : formattedData,
          };

          console.log('getData called, returning:', result);
          onSubmit(result);
          return result;
        },
      }),
      [product_type, products, productName, genericNames, species, onSubmit, ref],
    );

    useEffect(() => {
      if (productListData && productListData.catalog) {
        const catalog = productListData.catalog;
        if (product_type === 'MULTIPLE' && Array.isArray(catalog)) {
          const initialProducts = catalog.map((item, index) => ({
            key: item.product_id || `product-${index}`,
            productName: item.product_name,
            description: item.description,
            code: item.code,
            product_id: item.product_id,
            impQty: item.imp_qty,
            manQty: item.man_qty,
            status: item.status,
          }));
          setProducts(initialProducts);
          const initialCodes: Record<string, string> = {};
          catalog.forEach((item) => {
            if (item.product_id) {
              initialCodes[item.product_id] = item.code || '';
            }
          });
          setAafCodes(initialCodes);
        }
      }
    }, [productListData]);

    const getProductStatus = () => {
      if (productListData && productListData.catalog && product_type != null) {
        const catalog = productListData.catalog;
        if (product_type === 'MULTIPLE' && Array.isArray(catalog)) {
          return catalog.some((item) => item.status === 'ACTIVE') ? 'ACTIVE' : null;
        } else if (catalog.status) {
          return catalog.status;
        }
      }
      return null;
    };

    const handleAmendment = () => {
      if (!pr_id) {
        notification.error({
          message: 'Missing Information',
          description: 'Product registration ID is required to amend products.',
        });
        return;
      }

      let formattedData = null;
      let catalogProductIds: string[] = [];

      if (product_type !== null && product_type === 'MULTIPLE') {
        if (selectedRowKeys.length === 0) {
          notification.error({
            message: 'Selection Required',
            description: 'Please select at least one product to amend.',
          });
          return;
        }

        const productsToAmend = products.filter((p) => selectedRowKeys.includes(p.key));

        catalogProductIds = productsToAmend.filter((p) => p.product_id).map((p) => p.product_id as string);

        if (catalogProductIds.length === 0) {
          notification.error({
            message: 'Invalid Selection',
            description: 'Selected products must have valid product IDs to be amended.',
          });
          return;
        }
      } else {
        formattedData = {
          product_name: productName,
          generic_names: genericNames,
          species: species,
        };

        if (productListData && productListData.catalog && productListData.catalog.product_id) {
          catalogProductIds = [productListData.catalog.product_id];
        }
      }

      console.log('Amending with catalog_product_ids:', catalogProductIds);

      const catalogData: CatalogData = {
        catalog_product_ids: catalogProductIds,
      };

      console.log('Amending with data:', catalogData);

      amendProducts(Number(pr_id), catalogData)
        .then((response) => {
          notification.success({
            message: 'Amendment Successful',
            description: 'Product information has been amended successfully.',
          });
          navigate('/product-registrations');
        })
        .catch((error) => {
          notification.error({
            message: 'Amendment Failed',
            description: error.message || 'Failed to amend product information.',
          });
        });
    };

    const rowSelection =
      product_type !== null && product_type === 'MULTIPLE'
        ? {
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => {
              setSelectedRowKeys(newSelectedRowKeys);
            },
            getCheckboxProps: (record: ProductEntry) => ({
              disabled: !isAmmendable, // Disable checkbox when not ammendable
            }),
          }
        : undefined;

    const toggleEditMode = (productId: string) => {
      setEditableInputs((prev) => {
        const resetState: Record<string, boolean> = {};
        Object.keys(prev).forEach((key) => {
          resetState[key] = false;
        });

        return {
          ...resetState,
          [productId]: !prev[productId],
        };
      });
    };

    const handleInputChange = (productId: string, value: string) => {
      setAafCodes((prevCodes) => ({
        ...prevCodes,
        [productId]: value,
      }));
    };

    const handleAAF = async () => {
      try {
        setSubmitting(true);

        const updatedCatalogItems = Object.entries(aafCodes)
          .filter(([_, code]) => code && code.trim() !== '')
          .map(([productId, code]) => ({
            code: code,
            product_id: productId,
          }));

        if (updatedCatalogItems.length === 0) {
          notification.error({
            message: 'No Codes entered to update',
            description: 'Failed to update catalog.',
          });
          return;
        }

        const updatedCatalogData: updatedCatalog = {
          updated_catalog: updatedCatalogItems as any,
        };

        if (!pr_id) {
          notification.error({
            message: 'Missing Information',
            description: 'Product registration ID is required to update catalog.',
          });
          return;
        }

        const response = await updateCatalog(Number(pr_id), updatedCatalogData);

        const updatedProducts = products.map((product) => {
          const updatedItem = updatedCatalogItems.find((item) => item.product_id === product.product_id);
          if (updatedItem) {
            return {
              ...product,
              code: updatedItem.code,
            };
          }
          return product;
        });

        setProducts(updatedProducts);

        const newCodes: Record<string, string> = {};
        updatedCatalogItems.forEach((item) => {
          if (item.product_id && item.code) {
            newCodes[item.product_id] = item.code;
          }
        });

        setAafCodes({
          ...aafCodes,
          ...newCodes,
        });

        const resetEditState: Record<string, boolean> = {};
        Object.keys(editableInputs).forEach((key) => {
          resetEditState[key] = false;
        });
        setEditableInputs(resetEditState);

        notification.success({
          message: 'AAF Codes updated.',
          description: 'Product Catalog has been updated successfully.',
        });
      } catch (error) {
        console.error('Failed to update AAF codes:', error);
        notification.error({
          message: 'Update Failed',
          description: 'Failed to update product catalog.',
        });
      } finally {
        setSubmitting(false);
      }
    };

    useEffect(() => {
      if (products.length > 0) {
        const initialCodes: Record<string, string> = {};
        products.forEach((product) => {
          if (product.product_id) {
            initialCodes[product.product_id] = product.code || '';
          }
        });
        setAafCodes(initialCodes);
      }
    }, [products]);

    console.log('Requirements for AAF code: ', isCatEditable, userType, status);

    return (
      <Form
        form={form}
        name="productListForm"
        size="small"
        labelCol={{ lg: { span: 8 }, md: { span: 8 }, sm: { span: 24 } }}
        labelAlign="left"
        labelWrap
        wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
      >
        <>
          <BaseRow gutter={[16, 16]}>
            {product_type !== null && product_type === 'MULTIPLE' ? (
              <>
                {isFormEditable && (
                  <>
                    <BaseCol xs={24} md={8} lg={12}>
                      <Form.Item label="Trade Name" name="productName">
                        <BaseInput
                          value={productName}
                          onChange={(e) => setProductName(e.target.value)}
                          readOnly={!isFormEditable}
                        />
                      </Form.Item>
                    </BaseCol>
                    <BaseCol xs={24} md={8} lg={12}>
                      <Form.Item label="Description of the Product" name="description">
                        <BaseInput.TextArea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          readOnly={!isFormEditable}
                        />
                      </Form.Item>
                    </BaseCol>
                    {/* <BaseCol xs={24} md={8} lg={12}>
                      <Form.Item label="Imported Quantity" name="imp_qty">
                        <BaseInput
                          value={impQty}
                          onChange={(e) => setImpQty(e.target.value)}
                          readOnly={!isFormEditable}
                        />
                      </Form.Item>
                    </BaseCol>
                    <BaseCol xs={24} md={8} lg={12}>
                      <Form.Item label="Manufactured Quantity" name="man_qty">
                        <BaseInput
                          value={manQty}
                          onChange={(e) => setManQty(e.target.value)}
                          readOnly={!isFormEditable}
                        />
                      </Form.Item>
                    </BaseCol> */}
                    <BaseCol xs={24}>
                      <BaseButton
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddProduct}
                        disabled={!isFormEditable}
                      >
                        Add Product
                      </BaseButton>
                      <br />
                    </BaseCol>
                  </>
                )}
                <BaseCol xs={24}>
                  <Table
                    rowSelection={rowSelection}
                    dataSource={products}
                    size="small"
                    columns={[
                      {
                        title: 'Trade Name',
                        dataIndex: 'productName',
                        key: 'productName',
                        width: 200,
                      },
                      {
                        title: 'AAF Code',
                        dataIndex: 'code',
                        key: 'code',
                        width: 200,
                        render: (_, record) => (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            {isCatEditable ? (
                              <>
                                <BaseInput
                                  value={
                                    aafCodes[record.product_id || ''] !== undefined
                                      ? aafCodes[record.product_id || '']
                                      : record.code || ''
                                  }
                                  onChange={(e) => handleInputChange(record.product_id || '', e.target.value)}
                                  placeholder="Enter AAF code"
                                  disabled={!editableInputs[record.product_id || '']}
                                />
                                {editableInputs[record.product_id || ''] ? (
                                  <BaseButton
                                    type="text"
                                    icon={<CheckOutlined />}
                                    onClick={() => toggleEditMode(record.product_id || '')}
                                  />
                                ) : (
                                  <BaseButton
                                    type="text"
                                    icon={<EditOutlined />}
                                    onClick={() => toggleEditMode(record.product_id || '')}
                                  />
                                )}
                              </>
                            ) : (
                              <span>{record.code}</span>
                            )}
                          </div>
                        ),
                      },
                      {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description',
                        width: 400,
                        render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>,
                      },
                      { title: 'Status', dataIndex: 'status', key: 'status', width: 100 },
                      // { title: 'Imp Qty', dataIndex: 'impQty', key: 'impQty', width: 100 },
                      // { title: 'Man Qty', dataIndex: 'manQty', key: 'manQty', width: 100 },
                      ...(isFormEditable
                        ? [
                            {
                              title: 'Actions',
                              key: 'actions',
                              render: (_: any, record: { key: string }) => (
                                <BaseButton type="ghost" onClick={() => handleRemoveProduct(record.key)}>
                                  <DeleteOutlined />
                                </BaseButton>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                </BaseCol>
              </>
            ) : (
              <>
                <BaseCol xs={24} md={8} lg={12}>
                  <Form.Item label="Product Name" name="product_name">
                    <BaseInput
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      readOnly={!isFormEditable}
                    />
                  </Form.Item>
                </BaseCol>
                <BaseCol xs={24} md={8} lg={12}>
                  <Form.Item label="Generic Names" name="generic_names">
                    {isFormEditable ? (
                      <>
                        <Input
                          value={genericInput}
                          onChange={(e) => setGenericInput(e.target.value)}
                          suffix={<PlusOutlined onClick={handleAddGenericName} style={{ cursor: 'pointer' }} />}
                        />
                        <div style={{ marginTop: '5px' }}>
                          {genericNames.map((name) => (
                            <Tag closable key={name} onClose={() => handleRemoveGenericName(name)}>
                              {name}
                            </Tag>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div>
                        {genericNames.map((name) => (
                          <Tag key={name}>{name}</Tag>
                        ))}
                      </div>
                    )}
                  </Form.Item>
                </BaseCol>
                <BaseCol xs={24} md={8} lg={12}>
                  <Form.Item label="Species" name="species">
                    {isFormEditable ? (
                      <>
                        <Input
                          value={speciesInput}
                          onChange={(e) => setSpeciesInput(e.target.value)}
                          suffix={<PlusOutlined onClick={handleAddSpecies} style={{ cursor: 'pointer' }} />}
                        />
                        <div style={{ marginTop: '5px' }}>
                          {species.map((name) => (
                            <Tag closable key={name} onClose={() => handleRemoveSpecies(name)}>
                              {name}
                            </Tag>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div>
                        {species.map((name) => (
                          <Tag key={name}>{name}</Tag>
                        ))}
                      </div>
                    )}
                  </Form.Item>
                </BaseCol>
              </>
            )}
          </BaseRow>
        </>

        {isAmmendable && getProductStatus() !== 'PENDING_AMEND' && (
          <BaseCol xs={24} md={12}>
            <BaseButton type="primary" icon={<EditOutlined />} onClick={() => handleAmendment()}>
              Amend
            </BaseButton>
          </BaseCol>
        )}
        {isCatEditable &&
          userType === 'ADMIN' &&
          status === 'PENDING_COMMITTEE_REVIEW' &&
          product_type === 'MULTIPLE' && (
            <BaseButton type="primary" onClick={handleAAF} loading={submitting}>
              Update Catalog
            </BaseButton>
          )}
      </Form>
    );
  },
);

export default ProductListForm;
